// T-15 — builder de opciones de highcharts para Chart (todo excepto el Mapa).
//
// Extraído de `src/components/Chart.tsx`: el objeto `options` que alimenta
// `<HighchartsReact>`. Es puro (a partir del input devuelve el mismo objeto),
// así que se testea sin renderizar el componente. La rama Mapa ya está cubierta
// por `getMapChartOptions` (T-07).
//
// `Highcharts.numberFormat` se usa dentro del formatter del eje Y (solo cuando
// es relativo/porcentaje); en los tests se mockea `highcharts`.

import Highcharts from 'highcharts'
import {
  ChartTypesEnum,
  FinalSeries,
  isRelativeType,
  isSingleYearType,
  chartTypeMap,
} from '../config/indicators-model'

export type ChartOptionsInput = {
  selectedChartType: ChartTypesEnum
  longTitle: string
  selectedYear: number
  displayUnit: string
  minY: number
  maxY: number
  fullScreen: boolean
  categories: string[]
  series: FinalSeries[]
  /** `selectedSubChart.availableRange[0]` — solo usado en Waterfall. */
  waterfallStartYear?: number | string
  /** `selectedSubChart.startFromZero` */
  startFromZero?: boolean
  selectedUnit?: string
}

/** Construye las options de highcharts para cualquier tipo excepto Mapa
 *  (que usa `getMapChartOptions`). */
export function buildChartOptions(input: ChartOptionsInput): Record<string, unknown> {
  const {
    selectedChartType,
    longTitle,
    selectedYear,
    displayUnit,
    minY,
    maxY,
    fullScreen,
    categories,
    series,
    waterfallStartYear,
    startFromZero,
    selectedUnit,
  } = input

  return {
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            chart: {
              height: '400',
            },
            yAxis: {
              title: {
                text: undefined,
              },
              offset: 0,
              labels: {
                style: {
                  fontSize: '11px',
                },
              },
            },
            xAxis: {
              offset: 0,
              labels: {
                style: {
                  fontSize: '11px',
                },
              },
            },
            title: {
              style: {
                fontSize: '11px',
              },
            },
            legend: {
              itemStyle: {
                fontSize: '10px',
              },
            },
          },
        },
      ],
    },
    chart: {
      height: fullScreen ? '45%' : '600',
      type: chartTypeMap(selectedChartType),
      zoomType: 'xy',
      marginTop: 60,
      map: 'countries/es/es-all',
    },
    exporting: {
      enabled: false,
      sourceWidth: 1280,
      sourceHeight: 720,
      chartOptions: {
        legend: {
          maxHeight: null,
        },
      },
    },
    title: {
      y: 25,
      text: `${longTitle}${isSingleYearType(selectedChartType)
        ? `${selectedChartType === ChartTypesEnum.Waterfall ? ` ${waterfallStartYear}` : ''
        } - ${selectedYear}`
        : ''
        }`,
      widthAdjust: -90,
    },
    credits: false,
    tooltip: {
      valueDecimals: 2,
      valueSuffix: ` ${displayUnit}`,
      pointFormat:
        selectedChartType === ChartTypesEnum.Pie
          ? '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}</b> ({point.percentage:.1f} %)<br/>'
          : selectedChartType === ChartTypesEnum.Waterfall
            ? `<span style="color:{point.color}">\u25CF</span> ${selectedYear}: <b>{point.y}</b><br/>`
            : '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}</b><br/>',
    },
    plotOptions: {
      column: {
        stacking: selectedChartType === ChartTypesEnum.ColumnStacked ? 'normal' : undefined,
      },
      line: {
        lineWidth: 4,
      },
      spline: {
        lineWidth: 4,
        states: {
          hover: {
            lineWidth: 5,
          },
        },
        marker: {
          enabled: false,
        },
      },
      area: {
        stacking: 'normal',
      },
    },
    ...(selectedChartType !== ChartTypesEnum.Waterfall
      ? {
        xAxis: {
          categories,
        },
      }
      : { xAxis: { type: 'category', categories: null } }),
    yAxis: {
      visible: selectedChartType !== ChartTypesEnum.Pie,
      title: {
        text: displayUnit,
      },
      min: !startFromZero ? null : minY >= 0 ? 0 : null,
      minorGridLineWidth: 0,
      gridLineWidth: selectedChartType === ChartTypesEnum.Pie ? 0 : 1,
      labels: {
        formatter:
          isRelativeType(selectedChartType) || selectedUnit === '%'
            ? function () {
              // @ts-ignore
              const self = this as any

              if (self.value > 100) {
                return ''
              }

              return Highcharts.numberFormat(self.value, 0)
            }
            : null,
      },
      ...(isSingleYearType(selectedChartType)
        ? { max: maxY }
        : {
          max: null,
        }),
    },
    legend: {
      enabled: selectedChartType !== ChartTypesEnum.Waterfall,
      navigation: { enabled: true },
      itemWidth: 300,
      itemStyle: {
        textOverflow: 'undefined',
      },
      maxHeight: 100,
    },
    series,
  }
}